import React, { useState, useEffect, useRef } from 'react';

/* ------------------------------------------------------------------ *
 *  LogicMojo — AI & ML Program · Learner Success Stories
 *  Premium, fully-responsive testimonial / success-story carousel.
 *  Styling lives in a real <style> block with media queries so the
 *  layout responds to live resize (no module-load mutation hacks).
 * ------------------------------------------------------------------ */

const LOGO_BASE = '/react/ai-assets/images/companyLogo';

const reviews = [
  {
    id: 1,
    name: 'Piyush Mittal',
    company: 'Microsoft',
    designation: 'Software Engineer',
    tag: 'Career Switcher',
    logo: `${LOGO_BASE}/Microsoft-Logo.wine.svg`,
    avatarImage: '/react/ai-assets/images/piyush.png',
    rating: 5,
    package: '₹1.3 Cr',
    location: 'Hyderabad',
    highlights: ['Cracked Product Companies', 'Career Growth', 'AI / ML Upskilling'],
    review:
      'The AI & ML curriculum is of the best quality, with a brilliant learning experience from my mentor. The hands-on projects on Machine Learning, Deep Learning, and Generative AI prepared me perfectly for product-company interviews. I cracked Paytm, Adobe, Intuit, and Microsoft — and finally joined Microsoft IDC at ₹1.3 Cr.',
  },
  {
    id: 2,
    name: 'Priya Singh',
    company: 'Cisco',
    designation: 'Senior Engineer',
    tag: 'Working Professional',
    logo: `${LOGO_BASE}/Cisco_Systems-Logo.wine.svg`,
    avatarImage: '/react/ai-assets/images/priya1.png',
    rating: 5,
    package: '₹74 LPA',
    location: 'Bangalore',
    highlights: ['Curriculum Quality', 'Project-Based Learning', 'Job Switch Success'],
    review:
      'The curriculum is of the best quality, with excellent real-world ML and Generative AI problems built live in class. The mentorship and project resources stayed genuinely helpful even after I completed the program.',
  },
  {
    id: 3,
    name: 'Anjani Kumar',
    company: 'Amazon',
    designation: 'SDE',
    tag: 'Career Switcher',
    logo: `${LOGO_BASE}/Amazon_(company)-Logo.wine.svg`,
    avatarImage: '/react/ai-assets/images/anjani.png',
    rating: 5,
    package: '₹28 LPA',
    location: 'Bangalore',
    highlights: ['Cracked Product Companies', 'Mentor Support', 'Interview Prep'],
    review:
      'A very well-structured AI & ML program with amazing lecture delivery by the trainers. The expert team was always available to clear doubts on Machine Learning and Deep Learning. The live, project-driven training helped me crack Zynga and now Amazon.',
  },
  {
    id: 4,
    name: 'Siddharth Pande',
    company: 'Microsoft',
    designation: 'Software Engineer',
    tag: 'Working Professional',
    logo: `${LOGO_BASE}/Microsoft-Logo.wine.svg`,
    avatarImage: '/react/ai-assets/images/sid1.png',
    rating: 5,
    package: '₹40 LPA',
    location: 'Bangalore',
    highlights: ['AI & ML Projects', 'Interview Prep', 'Career Growth'],
    review:
      'One of the best resources for learning Machine Learning, Deep Learning, and Generative AI. This program helped me repeatedly during interview prep for Walmart, Oracle, and Microsoft. I genuinely did not need any other online material.',
  },
  {
    id: 5,
    name: 'Diwakar Choudhary',
    company: 'Walmart',
    designation: 'Software Engineer',
    tag: 'Career Switcher',
    logo: `${LOGO_BASE}/Walmart_Labs-Logo.wine.svg`,
    avatarImage: '/react/ai-assets/images/diwakar.png',
    rating: 5,
    package: '₹72 LPA',
    location: 'Bangalore',
    highlights: ['Project-Based Learning', 'Real Learning Experience', 'Job Switch Success'],
    review:
      'An excellent program for interview preparation — very straight to the point, with in-depth coverage of every AI and ML concept in the live classes. The focus on practical implementation of every ML, GenAI, and Agentic AI project really set it apart.',
  },
  {
    id: 6,
    name: 'Rajnish Kumar',
    company: 'Flipkart',
    designation: 'Software Engineer',
    tag: 'Working Professional',
    logo: `${LOGO_BASE}/Flipkart-Logo.wine.svg`,
    avatarImage: '/react/ai-assets/images/rajnish.jpg',
    rating: 5,
    package: '₹38 LPA',
    location: 'US',
    highlights: ['AI / ML Upskilling', 'Mentor Support', 'Career Growth'],
    review:
      'Great program! It definitely opened new doors in understanding how ML models and LLMs work and in building real Generative AI solutions for the projects. The live classes are highly recommended for working professionals.',
  },
  {
    id: 7,
    name: 'Aravindo Swain',
    company: 'Microsoft',
    designation: 'Software Engineer',
    tag: 'Working Professional',
    logo: `${LOGO_BASE}/Microsoft-Logo.wine.svg`,
    avatarImage: '/react/ai-assets/images/arvindo.jpg',
    rating: 5,
    package: '₹54 LPA',
    location: 'Bangalore',
    highlights: ['Clear Explanations', 'GenAI & LLMs', 'Interview Prep'],
    review:
      'The best part is the explanation by the trainer — concise and crystal clear. Great quality of online material and classes. It covers all the Machine Learning, Deep Learning, and Generative AI topics that get asked during interviews.',
  },
];

const FLOATING_TAGS = ['AI & ML', 'GenAI', 'LLM', 'RAG', 'Agentic AI'];

const InteractiveReviews = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(true);
  const [progress, setProgress] = useState(0);
  const SLIDE_MS = 7000;
  const animRef = useRef(null);

  const goTo = (i) => {
    setCurrentIndex(((i % reviews.length) + reviews.length) % reviews.length);
    setProgress(0);
  };
  const next = () => goTo(currentIndex + 1);
  const prev = () => goTo(currentIndex - 1);

  // Smooth, time-based progress (rAF) so the bar is in sync with autoplay.
  useEffect(() => {
    if (!isPlaying) return undefined;
    let start = null;
    const tick = (ts) => {
      if (start === null) start = ts;
      const pct = Math.min(((ts - start) / SLIDE_MS) * 100, 100);
      setProgress(pct);
      if (pct >= 100) {
        setCurrentIndex((p) => (p + 1) % reviews.length);
        setProgress(0);
        start = null;
      }
      animRef.current = requestAnimationFrame(tick);
    };
    animRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(animRef.current);
  }, [isPlaying, currentIndex]);

  const r = reviews[currentIndex];

  return (
    <section className="lm-rev">
      <style>{CSS}</style>

      {/* ---- ambient background ---- */}
      <div className="lm-rev__bg" aria-hidden="true">
        <div className="lm-rev__grid" />
        <div className="lm-rev__glow lm-rev__glow--a" />
        <div className="lm-rev__glow lm-rev__glow--b" />
        <div className="lm-rev__glow lm-rev__glow--c" />
        {FLOATING_TAGS.map((t, i) => (
          <span key={t} className={`lm-rev__chip lm-rev__chip--${i}`}>{t}</span>
        ))}
      </div>

      {/* ---- heading ---- */}
      <header className="lm-rev__head">
        <span className="lm-rev__eyebrow">
          <span className="lm-rev__eyebrow-dot" />
          Learner Success Stories
        </span>
        <h2 className="lm-rev__title">
          What Our Learners Say About the <span>AI &amp; ML Program</span>
        </h2>
        <p className="lm-rev__sub">
          Real success stories from learners who upgraded their careers through AI, ML,
          Generative AI, LLMs, RAG, and Agentic AI training.
        </p>
      </header>

      {/* ---- testimonial card ---- */}
      <div className="lm-rev__stage">
        <article className="lm-rev__card" key={currentIndex}>
          {/* identity panel */}
          <aside className="lm-rev__id">
            <div className="lm-rev__avatar-wrap">
              <span className="lm-rev__avatar-ring" />
              <div className="lm-rev__avatar">
                {r.avatarImage ? (
                  <img
                    src={r.avatarImage}
                    alt={r.name}
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'flex';
                    }}
                  />
                ) : null}
                <span className="lm-rev__avatar-fallback" style={{ display: r.avatarImage ? 'none' : 'flex' }}>
                  {r.name.split(' ').map((n) => n[0]).join('')}
                </span>
              </div>
              <span className="lm-rev__verified" title="Verified Learner">✓</span>
            </div>

            <h3 className="lm-rev__name">{r.name}</h3>
            <p className="lm-rev__role">{r.designation}</p>

            <div className="lm-rev__logo">
              <img src={r.logo} alt={r.company} onError={(e) => { e.target.parentNode.style.display = 'none'; }} />
            </div>

            <div className="lm-rev__pills">
              <span className="lm-rev__pill lm-rev__pill--pkg">{r.package}</span>
              <span className="lm-rev__pill lm-rev__pill--loc">📍 {r.location}</span>
              <span className="lm-rev__pill lm-rev__pill--tag">{r.tag}</span>
            </div>
          </aside>

          {/* content panel */}
          <div className="lm-rev__body">
            <div className="lm-rev__topline">
              <div className="lm-rev__stars" aria-label={`${r.rating} out of 5`}>
                {[...Array(5)].map((_, i) => (
                  <span key={i} className={i < r.rating ? 'on' : ''}>★</span>
                ))}
              </div>
              <span className="lm-rev__verified-badge">Verified Learner</span>
            </div>

            <span className="lm-rev__quote">“</span>
            <p className="lm-rev__text">{r.review}</p>

            <div className="lm-rev__highlights">
              {r.highlights.map((h) => (
                <span key={h} className="lm-rev__hl">{h}</span>
              ))}
            </div>
          </div>
        </article>

        {/* progress bar */}
        <div className="lm-rev__progress">
          <span className="lm-rev__progress-fill" style={{ width: `${progress}%` }} />
        </div>

        {/* controls */}
        <div className="lm-rev__nav">
          <button className="lm-rev__circle" onClick={prev} aria-label="Previous review">
            <span className="lm-rev__arrow lm-rev__arrow--left" />
          </button>

          <div className="lm-rev__center">
            <button className="lm-rev__play" onClick={() => setIsPlaying((p) => !p)} aria-label={isPlaying ? 'Pause' : 'Play'}>
              {isPlaying ? (
                <span className="lm-rev__pause"><i /><i /></span>
              ) : (
                <span className="lm-rev__triangle" />
              )}
            </button>
            <div className="lm-rev__dots">
              {reviews.map((_, i) => (
                <button
                  key={i}
                  className={`lm-rev__dot ${i === currentIndex ? 'active' : ''}`}
                  onClick={() => goTo(i)}
                  aria-label={`Go to review ${i + 1}`}
                />
              ))}
            </div>
          </div>

          <button className="lm-rev__circle" onClick={next} aria-label="Next review">
            <span className="lm-rev__arrow lm-rev__arrow--right" />
          </button>
        </div>
      </div>

      {/* ---- stats ---- */}
      <div className="lm-rev__stats">
        {[
          { n: '5000+', l: 'Learners Mentored' },
          { n: '50+', l: 'Top Companies' },
          { n: '4.9/5', l: 'Average Rating' },
          { n: '97%', l: 'Success Rate' },
        ].map((s) => (
          <div className="lm-rev__stat" key={s.l}>
            <div className="lm-rev__stat-n">{s.n}</div>
            <div className="lm-rev__stat-l">{s.l}</div>
          </div>
        ))}
      </div>

      {/* ---- cta ---- */}
      <div className="lm-rev__cta">
        <a href="https://calendly.com/logicmojo/schedule-call-back-from-experts-for-live-classes?month=2026-06" target="_default" rel="noopener noreferrer" className="lm-rev__cta-btn">
          Join the Next AI &amp; ML Batch
          <span className="lm-rev__cta-arrow">→</span>
        </a>
        <p className="lm-rev__cta-note">Rated highly by working professionals · Limited seats per cohort</p>
      </div>
    </section>
  );
};

/* ====================== styles ====================== */
const CSS = `
.lm-rev {
  --navy:#070b1c; --navy2:#0b1228; --ink:#e8ecff;
  --indigo:#6366f1; --blue:#3b82f6; --violet:#8b5cf6; --cyan:#22d3ee; --gold:#fbbf24;
  --line:rgba(124,150,255,.16); --glass:rgba(20,28,56,.55);
  position:relative; overflow:hidden; box-sizing:border-box;
  padding:clamp(48px,7vw,90px) clamp(16px,5vw,40px);
  font-family:-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,Inter,sans-serif;
  background:
    radial-gradient(900px 600px at 12% -5%, rgba(99,102,241,.20), transparent 60%),
    radial-gradient(900px 700px at 92% 8%, rgba(34,211,238,.14), transparent 55%),
    linear-gradient(180deg, #0a0f24 0%, #070b1c 55%, #05081a 100%);
  color:var(--ink);
}
.lm-rev *{box-sizing:border-box;}

/* ---- ambient bg ---- */
.lm-rev__bg{position:absolute; inset:0; pointer-events:none; z-index:0;}
.lm-rev__grid{
  position:absolute; inset:0; opacity:.5;
  background-image:linear-gradient(rgba(124,150,255,.06) 1px,transparent 1px),
                   linear-gradient(90deg, rgba(124,150,255,.06) 1px,transparent 1px);
  background-size:46px 46px;
  -webkit-mask-image:radial-gradient(ellipse 80% 60% at 50% 30%, #000 30%, transparent 80%);
          mask-image:radial-gradient(ellipse 80% 60% at 50% 30%, #000 30%, transparent 80%);
}
.lm-rev__glow{position:absolute; border-radius:50%; filter:blur(70px); opacity:.55;}
.lm-rev__glow--a{top:-60px; left:-40px; width:340px; height:340px; background:radial-gradient(circle,rgba(99,102,241,.5),transparent 70%); animation:lmFloat 14s ease-in-out infinite;}
.lm-rev__glow--b{bottom:-80px; right:-40px; width:420px; height:420px; background:radial-gradient(circle,rgba(34,211,238,.35),transparent 70%); animation:lmFloat 18s ease-in-out infinite reverse;}
.lm-rev__glow--c{top:40%; left:55%; width:260px; height:260px; background:radial-gradient(circle,rgba(139,92,246,.4),transparent 70%); animation:lmFloat 16s ease-in-out infinite;}

.lm-rev__chip{
  position:absolute; padding:7px 14px; border-radius:999px; font-size:.72rem; font-weight:700;
  letter-spacing:.3px; color:#c9d4ff; background:rgba(99,102,241,.10);
  border:1px solid rgba(124,150,255,.28); backdrop-filter:blur(8px);
  box-shadow:0 6px 22px rgba(0,0,0,.25); white-space:nowrap;
  animation:lmFloat 9s ease-in-out infinite;
}
.lm-rev__chip--0{top:13%; left:6%;  animation-delay:0s;}
.lm-rev__chip--1{top:24%; right:8%; animation-delay:1.2s;}
.lm-rev__chip--2{bottom:30%; left:4%; animation-delay:2.1s;}
.lm-rev__chip--3{top:60%; right:5%;  animation-delay:.6s;}
.lm-rev__chip--4{bottom:14%; right:14%; animation-delay:1.8s;}

/* ---- heading ---- */
.lm-rev__head{position:relative; z-index:2; text-align:center; max-width:760px; margin:0 auto clamp(34px,5vw,56px);}
.lm-rev__eyebrow{
  display:inline-flex; align-items:center; gap:8px; padding:7px 16px; border-radius:999px;
  font-size:.78rem; font-weight:700; letter-spacing:.6px; text-transform:uppercase;
  color:#bcd0ff; background:rgba(99,102,241,.12); border:1px solid rgba(124,150,255,.3);
  margin-bottom:20px;
}
.lm-rev__eyebrow-dot{width:8px; height:8px; border-radius:50%; background:var(--cyan); box-shadow:0 0 10px var(--cyan); animation:lmPulse 1.8s ease-in-out infinite;}
.lm-rev__title{
  margin:0 0 16px; font-weight:800; line-height:1.18; letter-spacing:-.5px;
  font-size:clamp(1.7rem,4.2vw,3rem); color:#fff;
}
.lm-rev__title span{
  background:linear-gradient(100deg,var(--cyan),var(--blue) 45%,var(--violet));
  -webkit-background-clip:text; background-clip:text; -webkit-text-fill-color:transparent;
}
.lm-rev__sub{margin:0 auto; max-width:620px; font-size:clamp(.95rem,1.6vw,1.12rem); line-height:1.65; color:rgba(214,222,255,.72);}

/* ---- stage / card ---- */
.lm-rev__stage{position:relative; z-index:2; max-width:1040px; margin:0 auto;}
.lm-rev__card{
  display:grid; grid-template-columns:300px 1fr; gap:clamp(24px,3vw,44px);
  padding:clamp(24px,3vw,44px);
  background:linear-gradient(180deg, rgba(24,33,66,.72), rgba(14,20,44,.62));
  border:1px solid var(--line); border-radius:28px;
  backdrop-filter:blur(22px); -webkit-backdrop-filter:blur(22px);
  box-shadow:0 30px 70px rgba(0,0,0,.5), inset 0 1px 0 rgba(255,255,255,.07);
  animation:lmCardIn .55s cubic-bezier(.2,.7,.2,1) both;
  position:relative; overflow:hidden;
}
.lm-rev__card::before{
  content:""; position:absolute; inset:0; border-radius:28px; padding:1px;
  background:linear-gradient(120deg, rgba(34,211,238,.55), rgba(99,102,241,.15) 40%, rgba(139,92,246,.5));
  -webkit-mask:linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0);
  -webkit-mask-composite:xor; mask-composite:exclude; pointer-events:none; opacity:.8;
}

/* identity panel */
.lm-rev__id{
  display:flex; flex-direction:column; align-items:center; text-align:center;
  padding:8px 6px; border-radius:20px;
  background:radial-gradient(120% 80% at 50% 0%, rgba(99,102,241,.16), transparent 70%);
}
.lm-rev__avatar-wrap{position:relative; width:122px; height:122px; margin-bottom:16px;}
.lm-rev__avatar-ring{
  position:absolute; inset:-4px; border-radius:50%;
  background:conic-gradient(from 0deg, var(--cyan), var(--blue), var(--violet), var(--cyan));
  animation:lmSpin 6s linear infinite; filter:blur(.5px);
}
.lm-rev__avatar{
  position:absolute; inset:0; border-radius:50%; overflow:hidden;
  border:4px solid #0b1228; background:linear-gradient(135deg,#3b3f88,#2a2f6b);
  display:flex; align-items:center; justify-content:center;
}
.lm-rev__avatar img{width:100%; height:100%; object-fit:cover; display:block;}
.lm-rev__avatar-fallback{font-size:2rem; font-weight:800; color:#fff; align-items:center; justify-content:center; width:100%; height:100%;}
.lm-rev__verified{
  position:absolute; bottom:4px; right:4px; width:30px; height:30px; border-radius:50%;
  background:linear-gradient(135deg,var(--cyan),var(--blue)); color:#05122a; font-weight:900; font-size:.9rem;
  display:flex; align-items:center; justify-content:center; border:3px solid #0b1228; box-shadow:0 4px 12px rgba(34,211,238,.5);
}
.lm-rev__name{margin:0 0 4px; font-size:1.35rem; font-weight:800; color:#fff;}
.lm-rev__role{margin:0 0 14px; font-size:.92rem; color:rgba(200,210,255,.65);}
.lm-rev__logo{
  height:34px; padding:6px 16px; margin-bottom:16px; border-radius:12px;
  background:rgba(255,255,255,.92); display:inline-flex; align-items:center; justify-content:center;
  box-shadow:0 6px 18px rgba(0,0,0,.3);
}
.lm-rev__logo img{max-height:22px; max-width:120px; object-fit:contain; display:block;}
.lm-rev__pills{display:flex; flex-wrap:wrap; gap:8px; justify-content:center;}
.lm-rev__pill{padding:6px 13px; border-radius:999px; font-size:.78rem; font-weight:700; border:1px solid; backdrop-filter:blur(6px);}
.lm-rev__pill--pkg{color:#34d399; background:rgba(16,185,129,.14); border-color:rgba(16,185,129,.4); box-shadow:0 0 16px rgba(16,185,129,.18);}
.lm-rev__pill--loc{color:#a5b4ff; background:rgba(99,102,241,.14); border-color:rgba(124,150,255,.4);}
.lm-rev__pill--tag{color:#5eead4; background:rgba(34,211,238,.12); border-color:rgba(34,211,238,.38);}

/* content panel */
.lm-rev__body{display:flex; flex-direction:column; justify-content:center; position:relative; min-width:0;}
.lm-rev__topline{display:flex; align-items:center; justify-content:space-between; gap:14px; margin-bottom:14px; flex-wrap:wrap;}
.lm-rev__stars{display:flex; gap:4px; font-size:1.3rem; line-height:1;}
.lm-rev__stars span{color:rgba(255,255,255,.16);}
.lm-rev__stars span.on{color:var(--gold); text-shadow:0 0 12px rgba(251,191,36,.5);}
.lm-rev__verified-badge{
  font-size:.72rem; font-weight:700; letter-spacing:.4px; color:#5eead4;
  padding:5px 12px; border-radius:999px; background:rgba(34,211,238,.1); border:1px solid rgba(34,211,238,.32);
}
.lm-rev__quote{
  display:block; font-family:Georgia,serif; font-size:4rem; line-height:.4; height:34px;
  color:rgba(124,150,255,.4); margin:6px 0 2px;
}
.lm-rev__text{
  margin:0 0 22px; font-size:clamp(1.02rem,1.7vw,1.28rem); line-height:1.72;
  color:rgba(236,240,255,.94); font-weight:400; max-width:60ch;
}
.lm-rev__highlights{display:flex; flex-wrap:wrap; gap:9px;}
.lm-rev__hl{
  padding:7px 14px; border-radius:10px; font-size:.8rem; font-weight:600; color:#cdd7ff;
  background:linear-gradient(135deg, rgba(99,102,241,.16), rgba(139,92,246,.12));
  border:1px solid rgba(124,150,255,.26);
}

/* progress */
.lm-rev__progress{height:5px; margin:22px auto 18px; max-width:520px; border-radius:99px; background:rgba(124,150,255,.12); overflow:hidden;}
.lm-rev__progress-fill{display:block; height:100%; border-radius:99px; background:linear-gradient(90deg,var(--cyan),var(--blue),var(--violet)); box-shadow:0 0 14px rgba(59,130,246,.6); transition:width .12s linear;}

/* nav */
.lm-rev__nav{display:flex; align-items:center; justify-content:center; gap:18px;}
.lm-rev__circle{
  width:50px; height:50px; flex:none; border-radius:50%; cursor:pointer;
  background:rgba(99,102,241,.12); border:1px solid rgba(124,150,255,.3);
  display:flex; align-items:center; justify-content:center; transition:all .25s ease; backdrop-filter:blur(8px);
}
.lm-rev__circle:hover{background:linear-gradient(135deg,var(--blue),var(--indigo)); transform:translateY(-2px); box-shadow:0 10px 26px rgba(59,130,246,.4);}
.lm-rev__arrow{width:11px; height:11px; border-top:2px solid #fff; border-left:2px solid #fff;}
.lm-rev__arrow--left{transform:rotate(-45deg); margin-left:4px;}
.lm-rev__arrow--right{transform:rotate(135deg); margin-right:4px;}
.lm-rev__center{display:flex; align-items:center; gap:16px;}
.lm-rev__play{
  width:42px; height:42px; border-radius:50%; cursor:pointer;
  background:rgba(34,211,238,.14); border:1px solid rgba(34,211,238,.34);
  display:flex; align-items:center; justify-content:center; transition:all .25s ease;
}
.lm-rev__play:hover{transform:scale(1.08); box-shadow:0 0 18px rgba(34,211,238,.4);}
.lm-rev__pause{display:flex; gap:3px;}
.lm-rev__pause i{width:3.5px; height:13px; background:#fff; border-radius:1px;}
.lm-rev__triangle{width:0; height:0; border-left:11px solid #fff; border-top:7px solid transparent; border-bottom:7px solid transparent; margin-left:3px;}
.lm-rev__dots{display:flex; align-items:center; gap:8px; flex-wrap:wrap; justify-content:center; max-width:200px;}
.lm-rev__dot{width:9px; height:9px; border-radius:50%; border:none; cursor:pointer; padding:0; background:rgba(255,255,255,.22); transition:all .3s ease;}
.lm-rev__dot:hover{background:rgba(255,255,255,.45);}
.lm-rev__dot.active{width:30px; border-radius:99px; background:linear-gradient(90deg,var(--cyan),var(--blue)); box-shadow:0 0 12px rgba(59,130,246,.6);}

/* stats */
.lm-rev__stats{
  position:relative; z-index:2; display:grid; grid-template-columns:repeat(4,1fr); gap:16px;
  max-width:840px; margin:clamp(40px,6vw,60px) auto 0;
}
.lm-rev__stat{
  text-align:center; padding:22px 12px; border-radius:18px;
  background:rgba(20,28,56,.5); border:1px solid var(--line); backdrop-filter:blur(14px);
  transition:transform .3s ease, box-shadow .3s ease;
}
.lm-rev__stat:hover{transform:translateY(-4px); box-shadow:0 16px 36px rgba(0,0,0,.35);}
.lm-rev__stat-n{font-size:clamp(1.5rem,3vw,2rem); font-weight:800; line-height:1; background:linear-gradient(120deg,var(--cyan),var(--violet)); -webkit-background-clip:text; background-clip:text; -webkit-text-fill-color:transparent;}
.lm-rev__stat-l{margin-top:8px; font-size:.82rem; color:rgba(210,218,255,.7); font-weight:500;}

/* cta */
.lm-rev__cta{position:relative; z-index:2; text-align:center; margin-top:clamp(34px,5vw,48px);}
.lm-rev__cta-btn{
  display:inline-flex; align-items:center; gap:10px; text-decoration:none;
  padding:16px 38px; border-radius:999px; font-size:1.05rem; font-weight:700; color:#fff;
  background:linear-gradient(120deg,var(--blue),var(--indigo) 55%,var(--violet));
  box-shadow:0 14px 38px rgba(79,70,229,.45); border:1px solid rgba(255,255,255,.18);
  transition:transform .25s ease, box-shadow .25s ease;
}
.lm-rev__cta-btn:hover{transform:translateY(-3px); box-shadow:0 20px 48px rgba(79,70,229,.6);}
.lm-rev__cta-arrow{transition:transform .25s ease;}
.lm-rev__cta-btn:hover .lm-rev__cta-arrow{transform:translateX(5px);}
.lm-rev__cta-note{margin:16px 0 0; font-size:.85rem; color:rgba(200,210,255,.6);}

/* ---- animations ---- */
@keyframes lmFloat{0%,100%{transform:translateY(0)}50%{transform:translateY(-16px)}}
@keyframes lmSpin{to{transform:rotate(360deg)}}
@keyframes lmPulse{0%,100%{opacity:1;transform:scale(1)}50%{opacity:.5;transform:scale(.7)}}
@keyframes lmCardIn{from{opacity:0; transform:translateY(22px) scale(.985)}to{opacity:1; transform:none}}

/* ============ responsive ============ */
@media (max-width:860px){
  .lm-rev__card{grid-template-columns:1fr; gap:22px; text-align:center;}
  .lm-rev__id{padding-bottom:6px; border-bottom:1px solid var(--line);}
  .lm-rev__body{text-align:left;}
  .lm-rev__text{max-width:none;}
}
@media (max-width:560px){
  .lm-rev__chip{display:none;}
  .lm-rev__card{padding:22px 18px; border-radius:22px;}
  .lm-rev__avatar-wrap{width:96px; height:96px;}
  .lm-rev__name{font-size:1.2rem;}
  .lm-rev__topline{justify-content:center;}
  .lm-rev__body{text-align:center;}
  .lm-rev__quote{margin:2px auto 0;}
  .lm-rev__highlights{justify-content:center;}
  .lm-rev__stars{font-size:1.15rem;}
  .lm-rev__text{font-size:1.02rem; line-height:1.66;}
  .lm-rev__stats{grid-template-columns:repeat(2,1fr); gap:12px;}
  .lm-rev__circle{width:44px; height:44px;}
  .lm-rev__center{gap:12px;}
  .lm-rev__dots{max-width:130px;}
  .lm-rev__cta-btn{padding:14px 28px; font-size:.98rem;}
}
@media (max-width:380px){
  .lm-rev__pills{gap:6px;}
  .lm-rev__pill{font-size:.72rem; padding:5px 10px;}
  .lm-rev__dot.active{width:24px;}
}
@media (prefers-reduced-motion:reduce){
  .lm-rev__glow,.lm-rev__chip,.lm-rev__avatar-ring,.lm-rev__eyebrow-dot,.lm-rev__card{animation:none !important;}
}
`;

export default InteractiveReviews;
